import {makeAutoObservable, reaction} from "mobx";
import {api, catchApi} from "../../api";
import {StorageTransferCreateDto, StorageTransferReasonDto,} from "../../api/sts";
import {StsCustomerItem} from "./StsCustomerStore";
import {ScaCustomerItem} from "../sca";
import {StsPrinterItem, stsZebraPrinterStore} from "./StsPrinterStore";
import {equals} from "../../utils";
import {validationStore} from "../ValidationStore";
import {stsCustomerCabinetStore} from "./StsCustomerCabinetStore";
import {userPrinterPreferenceStore} from "../ums";

const systemApi = api.sts.api

export interface StsCreateFormData {
    customer?: ScaCustomerItem
    printer?: StsPrinterItem
    reason?: StorageTransferReasonDto
    cabinet?: string
    vin: string
}

export interface StsCreateFormValidation {
    customer?: string
    printer?: string
    reason?: string
    vin?: string
    cabinet?: string
}

class StsCreateStore {
    get cabinet(): string | undefined {
        return this._cabinet;
    }

    set cabinet(value: string | undefined) {
        this._cabinet = value;
    }

    private get initial(): StorageTransferCreateDto {
        return this._initial;
    }

    private set initial(value: StorageTransferCreateDto) {
        this._initial = value;
    }
    get printer(): StsPrinterItem | undefined {
        return this._printer;
    }

    set printer(value: StsPrinterItem | undefined) {
        this._printer = value;
    }

    get vin(): string | undefined {
        return this._vin;
    }

    set vin(value: string | undefined) {
        this._vin = value;
    }

    get reason(): StorageTransferReasonDto {
        return this._reason;
    }

    set reason(value: StorageTransferReasonDto) {
        this._reason = value;
    }

    get customer(): StsCustomerItem | undefined {
        return this._customer;
    }

    set customer(value: StsCustomerItem | undefined) {
        this._customer = value;
        this.updateCabinets()
    }

    get correlationId(): string | undefined {
        return this._correlationId;
    }

    set correlationId(value: string | undefined) {
        this._correlationId = value;
    }

    get valid(): boolean {
        return this._valid;
    }

    private set valid(value: boolean) {
        this._valid = value;
    }

    get creating(): boolean {
        return this._creating;
    }

    private set creating(value: boolean) {
        this._creating = value;
    }

    get dirty(): boolean {
        return !equals(this.buildDto(), this.initial)
    }

    get formData(): StsCreateFormData {
        return {
            customer: this.customer,
            printer: this.printer,
            reason: this.reason,
            cabinet: this.cabinet,
            vin: this.vin || ''
        }
    }

    set formData(val: StsCreateFormData) {
        const {
            customer, printer, reason, vin, cabinet
        } = val
        this.customer = customer
        this.printer = printer ? printer : undefined
        this.cabinet = cabinet ? cabinet : undefined
        this.reason = reason || StorageTransferReasonDto.STORAGE_MERGE
        this.vin = vin ? vin : undefined
    }

    get formValidation(): StsCreateFormValidation {
        return {
            customer: validationStore.getFor('customerId'),
            printer: validationStore.getFor('labelPrinterId'),
            reason: validationStore.getFor('reason'),
            vin: validationStore.getFor('vin'),
            cabinet: validationStore.getFor('cabinet')
        }
    }

    private _creating: boolean = false
    private _valid: boolean = false

    private _correlationId: string | undefined
    private _customer: StsCustomerItem | undefined
    private _printer: StsPrinterItem | undefined
    private _reason: StorageTransferReasonDto = StorageTransferReasonDto.STORAGE_MERGE
    private _vin: string | undefined
    private _cabinet: string | undefined

    private _initial: StorageTransferCreateDto = this.buildDto()

    constructor() {
        makeAutoObservable(this)
        reaction(() => [
            userPrinterPreferenceStore.zebraPrinterId,
            stsZebraPrinterStore.options
        ], () => this.resetDefaultPrinter())
        this.reset()
    }

    resetDefaultPrinter() {
        const printerId = userPrinterPreferenceStore.zebraPrinterId
        const options = stsZebraPrinterStore.options
        this.printer = options.find(p => p.id === printerId) || undefined
    }

    async create(): Promise<string | undefined> {
        const dto = this.buildDto()
        this.creating = true
        const response = await catchApi(async () => await systemApi.createStorageTransfer(dto))
        this.creating = false
        return response?.data.id
    }

    async validate() {
        const dto = this.buildDto()

        await validationStore.validate(systemApi.validateStorageTransfer, dto)

        this.valid = Boolean(validationStore.valid)
    }

    reset() {
        this.correlationId = crypto.randomUUID()
        this.customer = undefined
        this.printer = undefined
        this.reason = StorageTransferReasonDto.STORAGE_MERGE
        this.vin = undefined
        this.cabinet = undefined
        this.initial = this.buildDto()
    }

    buildDto(): StorageTransferCreateDto {
        const {
            correlationId,
            customer,
            reason,
            printer,
            cabinet,
            vin
        } = this


        return {
            correlationId: correlationId!,
            customerId: customer?.id!,
            labelPrinterId: printer?.id!,
            reason,
            destinationCabinet: cabinet!,
            vin: vin!,
        }
    }

    private updateCabinets() {
        const customerId = this.customer?.id
        if (customerId) {
            stsCustomerCabinetStore.load(customerId).finally()
        } else {
            stsCustomerCabinetStore.reset()
        }
    }
}

const stsCreateStore = new StsCreateStore()

export {
    stsCreateStore
}
